import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowRight, ShieldCheck, LineChart, Lock, TrendingUp, TrendingDown, Loader2 } from "lucide-react";
import { api } from "../lib/api";
import { useApp } from "../lib/AppContext";

const HERO = "https://images.unsplash.com/photo-1651341050677-24dba59ce0fd?crop=entropy&cs=srgb&fm=jpg&q=80&w=1400";
const MKT = { BURSA: "Bursa", US: "US", HK: "HK", SG: "SGX" };

function Nav() {
  return (
    <header className="sticky top-0 z-30 border-b border-[#E2E8F0] bg-white/80 backdrop-blur-md">
      <div className="mx-auto flex max-w-6xl items-center justify-between px-5 py-3">
        <div className="flex min-w-0 items-center gap-2 font-display text-sm font-extrabold tracking-tight" data-testid="landing-brand"><img src="/logo.svg" alt="DMI" className="h-8 w-8 shrink-0" /><span className="hidden sm:inline">DMI <span className="text-blue-600">SHARE</span> INVESTMENT</span><span className="sm:hidden">DMI</span></div>
        <div className="flex shrink-0 items-center gap-2 whitespace-nowrap">
          <Link to="/login" className="rounded-sm px-3 py-1.5 text-sm text-slate-700 transition-colors hover:text-blue-600" data-testid="landing-login-link">Log masuk</Link>
          <Link to="/register" className="rounded-sm bg-blue-600 px-3 py-1.5 text-sm font-medium text-white transition-colors hover:bg-blue-700" data-testid="landing-register-nav">Mohon akses</Link>
        </div>
      </div>
    </header>
  );
}

function Hero({ d }) {
  return (
    <section className="relative overflow-hidden border-b border-[#E2E8F0]">
      <div className="absolute inset-0 opacity-[0.06]" style={{ backgroundImage: "linear-gradient(#2563EB 1px, transparent 1px), linear-gradient(90deg, #2563EB 1px, transparent 1px)", backgroundSize: "48px 48px" }} />
      <div className="relative mx-auto grid max-w-6xl gap-10 px-5 py-16 lg:grid-cols-[1.1fr_1fr] lg:py-24">
        <div className="fade-up">
          <div className="mono mb-5 inline-flex items-center gap-2 rounded-sm border border-blue-200 bg-blue-50 px-2 py-1 text-[11px] text-blue-700"><span className="h-1.5 w-1.5 rounded-full bg-blue-600 pulse-dot" />Bursa · US · HK · SGX</div>
          <h1 className="font-display text-4xl font-extrabold leading-[1.05] sm:text-5xl lg:text-6xl" data-testid="landing-title">Signal saham AI.<br /><span className="text-blue-600">Modal dilindungi dahulu.</span></h1>
          <p className="mt-6 max-w-lg text-base text-slate-600">DMI mengimbas {d?.total_scanned || "90+"} saham setiap beberapa minit dan hanya memberi signal BUY bila trend, momentum dan nisbah untung:risiko lulus. Jika tiada peluang yang jelas, jawapannya ialah NO TRADE.</p>
          <div className="mt-8 flex flex-wrap gap-3">
            <Link to="/register" className="group inline-flex items-center gap-2 rounded-sm bg-blue-600 px-5 py-3 text-sm font-semibold text-white transition-colors hover:bg-blue-700" data-testid="landing-register-hero">Mohon akses dashboard<ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" /></Link>
            <a href="#signals" className="inline-flex items-center rounded-sm border border-[#E2E8F0] px-5 py-3 text-sm text-slate-700 transition-colors hover:border-blue-600" data-testid="landing-see-signals">Lihat signal hari ini</a>
          </div>
        </div>
        <div className="relative fade-up" style={{ animationDelay: "120ms" }}>
          <img src={HERO} alt="Carta pasaran saham" className="h-72 w-full rounded-sm border border-[#E2E8F0] object-cover shadow-xl lg:h-full" />
          <div className="panel absolute -bottom-6 left-4 p-3 shadow-lg" data-testid="landing-counts">
            <div className="text-[10px] uppercase tracking-widest text-slate-500">Signal hari ini</div>
            <div className="mono mt-1 flex gap-4 text-lg"><span className="text-emerald-600">{d?.counts?.BUY ?? "–"} BUY</span><span className="text-red-600">{d?.counts?.SELL ?? "–"} SELL</span></div>
          </div>
        </div>
      </div>
    </section>
  );
}

function SignalChip({ r }) {
  const buy = r.action === "BUY";
  return (
    <div className="panel flex items-center justify-between gap-3 p-3 transition-transform hover:-translate-y-0.5" data-testid={`lite-signal-${r.symbol}`}>
      <div className="min-w-0"><div className="mono text-sm font-bold">{r.symbol}</div><div className="truncate text-[11px] text-slate-500">{r.name} · {MKT[r.exchange] || r.exchange}</div></div>
      <span className={`mono inline-flex shrink-0 items-center gap-1 rounded-sm px-2 py-1 text-[11px] font-bold ${buy ? "bg-emerald-50 text-emerald-700" : "bg-red-50 text-red-700"}`}>{buy ? <TrendingUp className="h-3 w-3" /> : <TrendingDown className="h-3 w-3" />}{r.action}</span>
    </div>
  );
}

function LiteSignals({ d }) {
  const [mkt, setMkt] = useState("ALL");
  const rows = (d?.rows || []).filter((r) => mkt === "ALL" || r.exchange === mkt);
  return (
    <section id="signals" className="mx-auto max-w-6xl px-5 py-20" data-testid="landing-lite-signals">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div><h2 className="font-display text-2xl font-extrabold sm:text-3xl">Lite Signal hari ini</h2>
          <p className="mt-2 text-sm text-slate-600">Nama saham dan arah sahaja. Harga masuk, stop-loss dan sasaran untung hanya untuk ahli yang diluluskan.</p></div>
        <div className="flex flex-wrap gap-1" data-testid="lite-market-filter">
          {["ALL", "BURSA", "US", "HK", "SG"].map((m) => (
            <button key={m} onClick={() => setMkt(m)} data-testid={`lite-filter-${m.toLowerCase()}`} className={`mono rounded-sm border px-2.5 py-1 text-[11px] transition-colors ${mkt === m ? "border-blue-600 bg-blue-600 text-white" : "border-[#E2E8F0] text-slate-600 hover:border-blue-600"}`}>{m === "ALL" ? "Semua" : MKT[m]}</button>
          ))}
        </div>
      </div>
      <div className="mt-8 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
        {!d ? <Loader2 className="h-5 w-5 animate-spin text-slate-400" /> : rows.length ? rows.map((r) => <SignalChip key={r.symbol} r={r} />)
          : <div className="text-sm text-slate-500" data-testid="lite-empty">Tiada signal BUY/SELL buat masa ini — AI memilih NO TRADE.</div>}
      </div>
      <p className="mono mt-4 text-[10px] text-slate-500" data-testid="lite-updated">Dikemas kini {d?.scanned_at ? new Date(d.scanned_at).toLocaleString("en-GB", { hour12: false }) : "—"} · SELL bermaksud keluar dari posisi yang dipegang, bukan jualan singkat.</p>
    </section>
  );
}

const STEPS = [
  [LineChart, "Imbas", "Trend, momentum, volum dan struktur harga disemak untuk setiap saham."],
  [ShieldCheck, "Tapis risiko", "Signal hanya keluar jika nisbah untung:risiko sekurang-kurangnya 2:1 dan ada stop-loss."],
  [Lock, "Ahli sahaja", "Daftar, tunggu kelulusan, dan dapatkan harga masuk, stop dan sasaran penuh."],
];

function How() {
  return (
    <section className="border-y border-[#E2E8F0] bg-[#F8FAFC]">
      <div className="mx-auto grid max-w-6xl gap-8 px-5 py-20 md:grid-cols-3">
        {STEPS.map(([I, t, b], i) => (
          <div key={t} className="fade-up" style={{ animationDelay: `${i * 90}ms` }} data-testid={`landing-step-${i}`}>
            <I className="h-6 w-6 text-blue-600" /><div className="mono mt-4 text-[11px] text-slate-400">0{i + 1}</div>
            <h3 className="mt-1 font-display text-lg font-bold">{t}</h3><p className="mt-2 text-sm text-slate-600">{b}</p>
          </div>
        ))}
      </div>
    </section>
  );
}

export default function Landing() {
  const { user } = useApp();
  const nav = useNavigate();
  const [d, setD] = useState(null);
  useEffect(() => { if (user) nav(user.role === "member" ? "/member" : "/", { replace: true }); }, [user, nav]);
  useEffect(() => { api.get("/public/lite-signals").then((r) => setD(r.data)).catch(() => setD({ rows: [] })); }, []);
  return (
    <div className="grain min-h-screen bg-white text-slate-900" data-testid="landing-page">
      <Nav /><Hero d={d} /><LiteSignals d={d} /><How />
      <section className="mx-auto max-w-6xl px-5 py-20 text-center">
        <h2 className="font-display text-2xl font-extrabold sm:text-3xl">Mahu butiran penuh setiap signal?</h2>
        <p className="mx-auto mt-3 max-w-xl text-sm text-slate-600">Daftar dengan emel anda. Pemilik DMI akan menyemak dan meluluskan akaun sebelum anda boleh masuk ke dashboard.</p>
        <Link to="/register" className="mt-6 inline-flex items-center gap-2 rounded-sm bg-blue-600 px-5 py-3 text-sm font-semibold text-white transition-colors hover:bg-blue-700" data-testid="landing-register-bottom">Mohon akses<ArrowRight className="h-4 w-4" /></Link>
      </section>
      <footer className="border-t border-[#E2E8F0] px-5 py-6 text-center text-[11px] text-slate-500" data-testid="landing-disclaimer">Signal ini untuk maklumat sahaja dan bukan nasihat pelaburan. Dagangan saham melibatkan risiko kerugian; tiada keuntungan dijamin. Harga Bursa, HK dan SGX mungkin lewat ~15 minit.</footer>
    </div>
  );
}
